import { useState, useEffect, useRef } from 'react';
import braintree from 'braintree-web';
import styles from './ApplePayButton.module.sass';

const ApplePayButton = ({ clientToken, checkoutTotal, onApprove, orderData, offer }) => {
  const [applePayInstance, setApplePayInstance] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    // Apple Pay requires HTTPS - skip on HTTP to avoid InvalidAccessError
    if (window.location.protocol !== 'https:') {
      setIsSupported(false);
      return;
    }

    // Check if Apple Pay is supported on this device/browser
    if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
      setIsSupported(false);
      return;
    }

    if (clientToken) {
      braintree.client.create(
        {
          authorization: clientToken,
        },
        (clientErr, clientInstance) => {
          if (clientErr) {
            console.error('Error creating Braintree client:', clientErr);
            return;
          }

          braintree.applePay.create(
            {
              client: clientInstance,
            },
            (applePayErr, applePayInst) => {
              if (applePayErr) {
                console.error('Error creating Apple Pay instance:', applePayErr);
                return;
              }

              setApplePayInstance(applePayInst);
              setIsSupported(true);
            },
          );
        },
      );
    }
  }, [clientToken]);

  const createApplePaySession = () => {
    if (!applePayInstance) {
      console.error('Apple Pay instance not ready');
      return;
    }

    const displayName = offer.applePayDisplayName || offer.brand || window.location.hostname;

    const paymentRequest = applePayInstance.createPaymentRequest({
      total: {
        label: displayName,
        amount: String(checkoutTotal),
      },
      requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
    });

    const session = new window.ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = function (event) {
      applePayInstance.performValidation(
        {
          validationURL: event.validationURL,
          displayName: displayName,
        },
        function (err, merchantSession) {
          if (err) {
            console.error('Error validating merchant:', err);
            session.abort();
            return;
          }
          session.completeMerchantValidation(merchantSession);
        },
      );
    };

    session.onpaymentauthorized = function (event) {
      const shippingContact = event.payment.shippingContact;

      applePayInstance.tokenize(
        {
          token: event.payment.token,
        },
        async function (tokenizeErr, payload) {
          if (tokenizeErr) {
            console.error('Error tokenizing Apple Pay:', tokenizeErr);
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            return;
          }

          // Build complete order data with all shipping info from Apple Pay
          const newOrderData = {
            ...orderData,
            shippingCity: shippingContact?.locality || '',
            shippingAddress1: shippingContact?.addressLines?.[0] || '',
            shippingAddress2: shippingContact?.addressLines?.[1] || '',
            firstName: shippingContact?.givenName || '',
            lastName: shippingContact?.familyName || '',
            email: shippingContact?.emailAddress || '',
            shippingZip: shippingContact?.postalCode || '',
            shippingState: shippingContact?.administrativeArea || '',
            shippingCountry: shippingContact?.countryCode?.toUpperCase() || 'US',
            phone: shippingContact?.phoneNumber?.replace(/^\+1/, '') || '',
            braintree_nonce: payload.nonce,
            creditCardType: 'applepay',
          };

          onApprove(newOrderData);
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        },
      );
    };

    session.oncancel = function () {
      // eslint-disable-next-line no-console
      console.log('Apple Pay session cancelled');
    };

    session.begin();
  };

  // Attach native click handler to custom element
  useEffect(() => {
    const button = buttonRef.current;
    if (button && applePayInstance) {
      button.addEventListener('click', createApplePaySession);
      return () => {
        button.removeEventListener('click', createApplePaySession);
      };
    }
  }, [applePayInstance, checkoutTotal, orderData, offer, onApprove]);

  if (!isSupported || !applePayInstance) {
    return null;
  }

  return (
    <div className={styles.container}>
      <apple-pay-button ref={buttonRef} buttonstyle='black' type='plain' locale='en-US' />
    </div>
  );
};

export default ApplePayButton;
